import type { FurnitureCatalogEntry } from "./types";

/** Width in cm per seat for sofas/tables — used for auto-sizing. */
export const SEAT_WIDTH_CM = 55;
/** Padding in cm added to seat-based width calculations. */
export const SEAT_PADDING_CM = 15;

export const FURNITURE_CATALOG: FurnitureCatalogEntry[] = [
  // Desks
  { key: "desk-single", label: "Desk", category: "desks", width: 120, height: 110, seatCount: 1, parametric: true, defaultParams: { widthCm: 120, depthCm: 60, count: 1, includeChairs: true } },
  { key: "desk-l", label: "L-Shape Desk", category: "desks", width: 160, height: 120, seatCount: 1, parametric: true, defaultParams: { widthCm: 160, depthCm: 120, includeChairs: true } },

  // Seating
  { key: "chair", label: "Office Chair", category: "seating", width: 50, height: 50, amenities: ["seat"], seatCount: 1 },
  { key: "chair-meeting", label: "Meeting Chair", category: "seating", width: 45, height: 45, amenities: ["seat"], seatCount: 1 },
  { key: "sofa", label: "Sofa", category: "seating", width: 140, height: 60, parametric: true, defaultParams: { seats: 2, depthCm: 60 }, seatCount: 2 },
  { key: "sofa-l", label: "L-Sofa", category: "seating", width: 200, height: 200, parametric: true, defaultParams: { seatsA: 3, seatsB: 3, depthCm: 60 }, seatCount: 6 },

  // Tables — one unified entry, shape + chairType configurable
  { key: "table", label: "Table", category: "tables", width: 160, height: 80, parametric: true,
    defaultParams: { size: 4, shape: "rectangle", includeChairs: true, chairType: "meeting", widthCm: 120, depthCm: 80, chairs: { top: 1, bottom: 1, left: 1, right: 1 } } },

  // Equipment (amenities)
  { key: "camera", label: "Camera", category: "equipment", width: 20, height: 20, amenities: ["camera"] },
  { key: "whiteboard", label: "Whiteboard", category: "equipment", width: 120, height: 10, parametric: true, defaultParams: { widthCm: 120 }, amenities: ["whiteboard"] },
  { key: "tv-screen", label: "TV / Screen", category: "equipment", width: 100, height: 5, parametric: true, defaultParams: { widthCm: 100 }, amenities: ["tv"] },
  { key: "tv-camera", label: "TV + Camera", category: "equipment", width: 100, height: 15, parametric: true, defaultParams: { widthCm: 100 }, amenities: ["tv", "camera"] },
  { key: "speaker", label: "Speaker", category: "equipment", width: 25, height: 25, amenities: ["speaker"] },
  { key: "projector", label: "Projector", category: "equipment", width: 40, height: 50, amenities: ["tv"] },
  { key: "projector-screen", label: "Proj. Screen", category: "equipment", width: 200, height: 10, parametric: true, defaultParams: { widthCm: 200 }, amenities: ["whiteboard"] },
  { key: "coffee-machine", label: "Coffee Machine", category: "equipment", width: 40, height: 35, amenities: ["coffee"] },
  { key: "espresso-machine", label: "Espresso Machine", category: "equipment", width: 35, height: 40, amenities: ["coffee"] },
  { key: "dishwasher", label: "Dishwasher", category: "equipment", width: 60, height: 45, amenities: ["dishwasher"] },
  { key: "microwave", label: "Microwave", category: "equipment", width: 45, height: 35, amenities: ["microwave"], parametric: true, defaultParams: { capacity: 1 } },
  { key: "fridge", label: "Fridge", category: "equipment", width: 60, height: 60, amenities: ["fridge"], parametric: true, defaultParams: { fridgeSize: "full" } },
  { key: "recycling-bin", label: "Recycling Bin", category: "equipment", width: 35, height: 35, parametric: true, defaultParams: { wasteType: "general" }, amenities: ["recycling"] },
  // Papercut Hive printers — placed from the editor's "Unplaced printers"
  // tray, which stamps params.papercutPrinterId so the row links back to the
  // Hive record (backend PapercutPrinter.equipment + chat find_equipment).
  { key: "printer", label: "Printer", category: "equipment", width: 60, height: 50, amenities: ["printer"] },
  { key: "bookshelf", label: "Bookshelf", category: "equipment", width: 120, height: 40 },

  // Safety equipment — surfaced together in the palette and queried in
  // bulk by the dashboard (`/api/crm/equipment/safety/upcoming-inspections`).
  // alwaysVisible so they stay on the floorplan when furniture layer is off.
  { key: "fire-extinguisher", label: "Fire Extinguisher", category: "safety", width: 33, height: 33, alwaysVisible: true, parametric: true, defaultParams: { extinguisherClass: "ABC", inspectionIntervalMonths: 12 }, amenities: ["fire-extinguisher"] },
  { key: "first-aid-kit",     label: "First Aid Kit",     category: "safety", width: 50, height: 33, alwaysVisible: true, parametric: true, defaultParams: { inspectionIntervalMonths: 3 }, amenities: ["first-aid"] },
  { key: "aed",               label: "AED",               category: "safety", width: 50, height: 50, alwaysVisible: true, parametric: true, defaultParams: { inspectionIntervalMonths: 1 }, amenities: ["aed"] },

  // Storage
  { key: "cabinet", label: "Cabinet", category: "storage", width: 80, height: 40 },
  { key: "partition", label: "Partition", category: "storage", width: 150, height: 10, parametric: true, defaultParams: { length: 150 } },

  // Decor & Booths
  { key: "plant", label: "Plant", category: "decor", width: 40, height: 40 },
  { key: "booth-1p", label: "Phone Booth 1p", category: "decor", width: 120, height: 120, alwaysVisible: true },
  { key: "booth-2p", label: "Phone Booth 2p", category: "decor", width: 120, height: 160, alwaysVisible: true },
];

export function getCatalogEntry(key: string): FurnitureCatalogEntry | undefined {
  return FURNITURE_CATALOG.find((e) => e.key === key);
}

/**
 * Which backend model owns this catalog kind. Mirrors the routing in
 * `frontend/src/lib/api/floorplanItems.ts::backendKindForType` but lives in
 * the package so the renderer can gate visibility per-model.
 *
 *   category=desks                  → Desk model      → "desks" layer
 *   category=equipment | safety     → Equipment model → "equipment" layer
 *   everything else                 → Furniture model → "furniture" layer
 *
 * Items with `alwaysVisible: true` (fire extinguisher / first aid / AED /
 * booths) bypass layer toggles entirely — that's a separate per-item
 * override, not a layer membership.
 */
export type BackendKind = "desk" | "equipment" | "furniture";

export function getBackendKindForType(type: string): BackendKind {
  const entry = getCatalogEntry(type);
  if (!entry) return "furniture";
  if (entry.category === "desks") return "desk";
  if (entry.category === "equipment" || entry.category === "safety") return "equipment";
  return "furniture";
}

export function getCatalogByCategory(): Map<string, FurnitureCatalogEntry[]> {
  const map = new Map<string, FurnitureCatalogEntry[]>();
  for (const entry of FURNITURE_CATALOG) {
    if (!map.has(entry.category)) map.set(entry.category, []);
    map.get(entry.category)!.push(entry);
  }
  return map;
}

/** Human-readable labels for amenity keys. */
export const AMENITY_LABELS: Record<string, string> = {
  tv: "TV / Screen",
  camera: "Camera",
  speaker: "Speaker",
  whiteboard: "Whiteboard",
  coffee: "Coffee",
  dishwasher: "Dishwasher",
  microwave: "Microwave",
  fridge: "Fridge",
  recycling: "Recycling",
  printer: "Printer",
  seat: "Seat",
  "fire-extinguisher": "Fire Extinguisher",
  "first-aid": "First Aid",
  aed: "AED",
};

/** Equipment kinds that share inspection / safety tracking. Mirrors
 * SAFETY_KINDS in backend/crm/api/equipment.py — keep in sync. */
export const SAFETY_EQUIPMENT_KINDS = [
  "fire-extinguisher",
  "first-aid-kit",
  "aed",
] as const;
export type SafetyEquipmentKind = (typeof SAFETY_EQUIPMENT_KINDS)[number];

export interface AmenityStatus {
  available: boolean;
  broken: boolean;
  count: number;
  brokenCount: number;
  repairEta?: string;
  note?: string;
}

/** Compute room amenities from placed furniture. */
export function computeAmenities(furniture: { type: string; params?: import("./types").FurnitureParams; brokenAmenities?: import("./types").BrokenAmenity[] }[]): {
  amenities: Record<string, AmenityStatus>;
  seatCount: number;
} {
  const counts = new Map<string, number>();
  const brokenCounts = new Map<string, number>();
  let seatCount = 0;

  for (const item of furniture) {
    const entry = getCatalogEntry(item.type);
    if (!entry) continue;

    // Collect amenities this item provides (with quantity)
    const qty = item.params?.capacity ?? 1;
    for (const a of entry.amenities ?? []) {
      counts.set(a, (counts.get(a) ?? 0) + qty);
    }

    // Count broken amenities (supports indexed keys like "microwave:0")
    for (const b of item.brokenAmenities ?? []) {
      const baseAmenity = b.amenity.split(":")[0];
      brokenCounts.set(baseAmenity, (brokenCounts.get(baseAmenity) ?? 0) + 1);
    }

    if (entry.seatCount && item.type !== "sofa" && item.type !== "sofa-l") {
      const count = item.params?.count || 1;
      seatCount += entry.seatCount * count;
    }
    // Sofa seats from params
    if (item.type === "sofa") {
      seatCount += item.params?.seats || entry.seatCount || 2;
    }
    // L-sofa: seatsA + seatsB + 1 (corner)
    if (item.type === "sofa-l") {
      const seatsA = item.params?.seatsA || 3;
      const seatsB = item.params?.seatsB || 3;
      seatCount += seatsA + seatsB + 1;
    }
    // Tables with chairs
    if (item.type === "table" && item.params?.includeChairs !== false) {
      const chairs = item.params?.chairs;
      if (chairs) {
        seatCount += (chairs.top || 0) + (chairs.bottom || 0) + (chairs.left || 0) + (chairs.right || 0);
      } else {
        seatCount += item.params?.size || 0;
      }
    }
  }

  // Build amenity status map
  const amenities: Record<string, AmenityStatus> = {};
  for (const [a, count] of counts) {
    const bc = brokenCounts.get(a) ?? 0;
    amenities[a] = {
      available: bc < count,
      broken: bc > 0,
      count,
      brokenCount: bc,
    };
  }

  return { amenities, seatCount };
}
